import React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Check, ChevronDown, Globe } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { LANGS, LANG_SWITCH, t, useLang } from "../lib/i18n";

// The language picker: a pill (globe, the language's code, a chevron) that
// opens the list of languages, each written in its own words. The pill has the
// same width in every language, and it and its list are LTR, as are the bars
// that hold it (Landing, JoinRoom, Lobby, the game's "Infos"; Room centres it):
// it stays at the same place in the 3 languages (owner's rule).
export const LanguageSwitch = ({ className = "" }) => {
  const { lang, setLang } = useLang();

  const choose = (code) => {
    if (code === lang) return;
    setLang(code);
    toast(t("lang.switched"));
  };

  // Hidden while LANG_SWITCH is off (see lib/i18n/core.js).
  if (!LANG_SWITCH) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          dir="ltr"
          aria-label={t("lang.label")}
          title={LANGS[lang].name}
          data-testid="language-switch"
          className={`group inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-white/[0.08] bg-slate-900/60 pe-2 ps-2.5 text-[11px] font-semibold text-slate-200 transition-colors hover:border-amber-500/40 hover:text-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50 data-[state=open]:border-amber-500/50 data-[state=open]:text-amber-100 ${className}`}
        >
          <Globe className="h-3.5 w-3.5 text-amber-300/90" />
          <span className="w-5 text-center tracking-wide">{LANGS[lang].short}</span>
          <ChevronDown className="h-3 w-3 text-slate-500 transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        dir="ltr"
        align="end"
        sideOffset={6}
        className="min-w-[10.5rem] rounded-xl border-white/10 bg-slate-900/95 p-1 text-slate-200 shadow-xl shadow-black/40 backdrop-blur-md"
      >
        <DropdownMenuPrimitive.RadioGroup value={lang} onValueChange={choose}>
          {Object.values(LANGS).map(({ code, short, name, htmlLang, dir }) => (
            <DropdownMenuPrimitive.RadioItem
              key={code}
              value={code}
              data-testid={`language-${code}`}
              className="flex cursor-pointer select-none items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] text-slate-300 outline-none transition-colors focus:bg-amber-500/10 focus:text-amber-100 data-[state=checked]:text-amber-200"
            >
              <span className="w-7 rounded-md bg-white/[0.06] py-0.5 text-center text-[10px] font-bold tracking-wide text-slate-400">
                {short}
              </span>
              <span lang={htmlLang} dir={dir} className="flex-1 text-left">{name}</span>
              <DropdownMenuPrimitive.ItemIndicator>
                <Check className="h-3.5 w-3.5 text-amber-400" />
              </DropdownMenuPrimitive.ItemIndicator>
            </DropdownMenuPrimitive.RadioItem>
          ))}
        </DropdownMenuPrimitive.RadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitch;
